import { today } from 'shared/src/date-string-utils/date-utils';
import { ISODateRange, ISODateString } from 'shared/src/date-string-utils/types';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';
import { PacingType } from 'shared/src/domain/line-item/line-item-types';

export type ExecutionStatus =
  | 'no_execution'
  | 'execution_without_media_plans'
  | 'execution_with_media_plans';

export type PacingValidatorExisting = {
  execution_status: ExecutionStatus;
  dates: ISODateRange;
  pacing_type: PacingType;
  pacing_details: PacingBlockV2[];
};

export type PacingValidatorUpdate = {
  dates: ISODateRange;
  pacing_type: PacingType;
  pacing_details: PacingBlockV2[];
  totalPaced: number;
  priceFloor: number | null;
};

export type PacingValidatorSpend = { day: ISODateString; amount: number }[];

export type IssueCode =
  | 'flight_start_after_first_spend'
  | 'flight_end_before_last_spend'
  | 'pacing_type_locked'
  | 'locked_block_modified'
  | 'locked_block_removed'
  | 'current_block_removed'
  | 'current_block_dates_modified'
  | 'block_outside_flight'
  | 'blocks_overlap'
  | 'totals_mismatch'
  | 'price_below_billable_revenue'
  | 'block_below_billable_revenue';

export type IssueSeverity = 'error' | 'warning';

export type ValidationIssue = {
  path: string;
  code: IssueCode;
  severity: IssueSeverity;
  message: string;
};

export type PacingValidationResult = {
  ok: boolean;
  issues: ValidationIssue[];
};

export function validateLineItemPacing(
  existing: PacingValidatorExisting | null,
  update: PacingValidatorUpdate,
  spend: PacingValidatorSpend
): PacingValidationResult {
  const now = today();
  const flight = update.dates;
  const blocks = update.pacing_details;
  const isLocked = existing != null && existing.execution_status !== 'no_execution';

  const lockedIssues =
    isLocked && existing
      ? [
          ...validateFlightDates(flight, spend),
          ...validatePacingTypeChange(existing, update, spend),
          ...validateBlockImmutability(existing.pacing_details, blocks, now, spend),
          ...validatePriceFloor(update, spend),
          ...validateCurrentBlockFloor(blocks, spend, now)
        ]
      : [];

  const issues: ValidationIssue[] = [
    ...lockedIssues,
    ...validateContainment(blocks, flight),
    ...validateNoOverlap(blocks),
    ...validateTotalsMatch(blocks, update.totalPaced)
  ];

  const ok = !issues.some(i => i.severity === 'error');
  return { ok, issues };
}

type LockedBlockKind = 'past' | 'current';

function blockPath(index: number, field?: 'dates' | 'budget'): string {
  const base = `pacing_details[${index}]`;
  return field ? `${base}.${field}` : base;
}

function validateFlightDates(flight: ISODateRange, spend: PacingValidatorSpend): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  const first = firstSpendDate(spend);
  if (first !== null && flight.from > first) {
    issues.push({
      path: 'dates.from',
      code: 'flight_start_after_first_spend',
      severity: 'warning',
      message: `Flight start date is after the first billable revenue date (${first}); this range now excludes recorded spend`
    });
  }

  const last = lastSpendDate(spend);
  if (last !== null && flight.to < last) {
    issues.push({
      path: 'dates.to',
      code: 'flight_end_before_last_spend',
      severity: 'warning',
      message: `Flight end date is before the last billable revenue date (${last}); this range now excludes recorded spend`
    });
  }
  return issues;
}

export function firstSpendDate(spend: PacingValidatorSpend): ISODateString | null {
  let first: ISODateString | null = null;
  for (const d of spend) {
    if (d.amount > 0 && (first === null || d.day < first)) first = d.day;
  }
  return first;
}

export function lastSpendDate(spend: PacingValidatorSpend): ISODateString | null {
  let last: ISODateString | null = null;
  for (const d of spend) {
    if (d.amount > 0 && (last === null || d.day > last)) last = d.day;
  }
  return last;
}

function validatePacingTypeChange(
  existing: PacingValidatorExisting,
  update: PacingValidatorUpdate,
  spend: PacingValidatorSpend
): ValidationIssue[] {
  const customToLifetime = existing.pacing_type === 'custom' && update.pacing_type === 'lifetime';
  if (!customToLifetime) return [];
  if (!spend.some(d => d.amount > 0)) return [];
  return [
    {
      path: 'pacing_type',
      code: 'pacing_type_locked',
      severity: 'error',
      message: 'Pacing type cannot change from custom to lifetime once spend has accrued'
    }
  ];
}

function validateBlockImmutability(
  existingBlocks: PacingBlockV2[],
  nextBlocks: PacingBlockV2[],
  now: string,
  spend: PacingValidatorSpend
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const nextByFrom = new Map(nextBlocks.map(b => [b.dates.from, b]));

  existingBlocks.forEach((block, index) => {
    const kind = lockedKind(block, now, spend);
    if (!kind) return;
    const next = nextByFrom.get(block.dates.from);
    if (!next) {
      issues.push(blockRemovedIssue(block, index, kind));
      return;
    }
    issues.push(...blockModifiedIssues(block, next, index, kind));
  });

  return issues;
}

function lockedKind(
  block: PacingBlockV2,
  now: string,
  spend: PacingValidatorSpend
): LockedBlockKind | null {
  if (block.dates.to <= now) return blockHadRevenue(block, spend) ? 'past' : null;
  if (block.dates.from <= now) return 'current';
  return null;
}

function blockHadRevenue(block: PacingBlockV2, spend: PacingValidatorSpend): boolean {
  return spend.some(d => d.amount > 0 && d.day >= block.dates.from && d.day <= block.dates.to);
}

function blockRemovedIssue(
  existing: PacingBlockV2,
  index: number,
  kind: LockedBlockKind
): ValidationIssue {
  if (kind === 'past') {
    return {
      path: blockPath(index),
      code: 'locked_block_removed',
      severity: 'warning',
      message: `Past pacing block starting ${existing.dates.from} has billable revenue; removal is informational`
    };
  }
  return {
    path: blockPath(index),
    code: 'current_block_removed',
    severity: 'warning',
    message: `Current pacing block starting ${existing.dates.from} was removed`
  };
}

function blockModifiedIssues(
  existing: PacingBlockV2,
  next: PacingBlockV2,
  index: number,
  kind: LockedBlockKind
): ValidationIssue[] {
  if (kind === 'current') {
    if (next.dates.to === existing.dates.to && next.dates.from === existing.dates.from) {
      return [];
    }
    return [
      {
        path: blockPath(index, 'dates'),
        code: 'current_block_dates_modified',
        severity: 'warning',
        message: `Current pacing block starting ${existing.dates.from} dates were modified`
      }
    ];
  }

  const issues: ValidationIssue[] = [];
  if (next.dates.to !== existing.dates.to) {
    issues.push({
      path: blockPath(index, 'dates'),
      code: 'locked_block_modified',
      severity: 'warning',
      message: `Past pacing block starting ${existing.dates.from} has billable revenue; date edit is informational`
    });
  }
  if (next.budget !== existing.budget) {
    issues.push({
      path: blockPath(index, 'budget'),
      code: 'locked_block_modified',
      severity: 'warning',
      message: `Past pacing block starting ${existing.dates.from} has billable revenue; budget edit is informational`
    });
  }
  return issues;
}

function validatePriceFloor(
  update: PacingValidatorUpdate,
  spend: PacingValidatorSpend
): ValidationIssue[] {
  if (update.priceFloor == null) return [];
  const totalSpend = sumSpend(spend);
  if (update.priceFloor + 0.01 >= totalSpend) return [];
  return [
    {
      path: 'price',
      code: 'price_below_billable_revenue',
      severity: 'warning',
      message: 'Price is below billable revenue to date'
    }
  ];
}

function sumSpend(spend: PacingValidatorSpend): number {
  return spend.reduce((sum, d) => sum + d.amount, 0);
}

function validateCurrentBlockFloor(
  blocks: PacingBlockV2[],
  spend: PacingValidatorSpend,
  now: string
): ValidationIssue[] {
  const currentIndex = blocks.findIndex(b => b.dates.from <= now && now <= b.dates.to);
  if (currentIndex === -1) return [];
  const current = blocks[currentIndex];
  const windowSpend = sumSpendInRange(spend, current.dates);
  if (current.budget + 0.01 >= windowSpend) return [];
  return [
    {
      path: blockPath(currentIndex, 'budget'),
      code: 'block_below_billable_revenue',
      severity: 'warning',
      message: `Pacing block starting ${current.dates.from} budget is below billable revenue`
    }
  ];
}

function sumSpendInRange(spend: PacingValidatorSpend, range: ISODateRange): number {
  return spend.reduce(
    (sum, d) => (d.day >= range.from && d.day <= range.to ? sum + d.amount : sum),
    0
  );
}

function validateContainment(blocks: PacingBlockV2[], flight: ISODateRange): ValidationIssue[] {
  return blocks.flatMap((b, index) => {
    if (b.dates.from >= flight.from && b.dates.to <= flight.to) return [];
    return [
      {
        path: blockPath(index),
        code: 'block_outside_flight' as const,
        severity: 'error' as const,
        message: `Pacing block ${b.dates.from}—${b.dates.to} is outside the flight`
      }
    ];
  });
}

function validateNoOverlap(blocks: PacingBlockV2[]): ValidationIssue[] {
  const sorted = blocks
    .map((b, index) => ({ b, index }))
    .sort((a, c) => (a.b.dates.from < c.b.dates.from ? -1 : 1));
  const issues: ValidationIssue[] = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (curr.b.dates.from <= prev.b.dates.to) {
      issues.push({
        path: blockPath(curr.index),
        code: 'blocks_overlap',
        severity: 'error',
        message: `Pacing block starting ${curr.b.dates.from} overlaps the previous block`
      });
    }
  }
  return issues;
}

function validateTotalsMatch(blocks: PacingBlockV2[], totalPaced: number): ValidationIssue[] {
  const totalNext = blocks.reduce((sum, b) => sum + b.budget, 0);
  if (Math.abs(totalNext - totalPaced) <= 0.01) return [];
  return [
    {
      path: 'pacing_details',
      code: 'totals_mismatch',
      severity: 'error',
      message: 'Pacing block budgets must sum to the line item total'
    }
  ];
}
